import React, { useEffect, useState } from 'react';
import './LeadsTab.css';

const BUSINESS_VERTICALS = ['Barber', 'Dentist', 'Unknown'];
const PAGE_SIZE = 25;

function normalizeUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `https://${url}`;
}

function splitPlatforms(value) {
  if (!value) return [];
  return value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
}

function LeadsTab({ apiUrl }) {
  const [selectedVertical, setSelectedVertical] = useState(
    BUSINESS_VERTICALS[0]
  );
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    address: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    website: '',
    bookingPlatform: '',
    email: '',
    businessHours: ''
  });
  const [moveLead, setMoveLead] = useState(null);
  const [moveVerticalValue, setMoveVerticalValue] = useState(BUSINESS_VERTICALS[0]);
  const [deleteLead, setDeleteLead] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [globalDownloadLoading, setGlobalDownloadLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [crmSyncLoading, setCrmSyncLoading] = useState(false);
  const [crmSyncResult, setCrmSyncResult] = useState(null);
  const [expandedLeadId, setExpandedLeadId] = useState(null);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setPage(1);
      setSearchTerm(searchInput.trim());
    }, 400);
    return () => clearTimeout(debounce);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchLeads() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          vertical: selectedVertical,
          page: String(page),
          limit: String(PAGE_SIZE)
        });
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        const response = await fetch(`${apiUrl}/api/leads?${params.toString()}`, {
          signal: controller.signal
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'Failed to fetch leads');
        }

        const payload = await response.json();
        setLeads(payload.data || []);
        setMeta(payload.meta || { total: 0, totalPages: 0 });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchLeads();
    return () => controller.abort();
  }, [apiUrl, page, searchTerm, selectedVertical, refreshKey]);

  const handleDownload = async () => {
    try {
      const params = new URLSearchParams({
        vertical: selectedVertical
      });
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      const response = await fetch(`${apiUrl}/api/leads/export?${params.toString()}`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to download leads');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `leads-${selectedVertical.toLowerCase()}-${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownloadAll = async () => {
    try {
      setGlobalDownloadLoading(true);
      const response = await fetch(`${apiUrl}/api/leads/export-all`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to download all leads');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `leads-all-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setGlobalDownloadLoading(false);
    }
  };

  const refreshLeads = () => {
    setRefreshKey(prev => prev + 1);
  };

  const openEditModal = (lead) => {
    setEditLead(lead);
    setEditForm({
      name: lead.name || '',
      address: lead.address || '',
      street: lead.street || '',
      city: lead.city || '',
      state: lead.state || '',
      zip: lead.zip || '',
      phone: lead.phone || '',
      website: lead.website || '',
      bookingPlatform: lead.bookingPlatform || '',
      email: lead.email || '',
      businessHours: lead.businessHours || ''
    });
  };

  const handleEditInputChange = (event) => {
    const { name, value } = event.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (!editLead) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/leads/${editLead.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to update lead');
      }
      setEditLead(null);
      refreshLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const openMoveModal = (lead) => {
    setMoveLead(lead);
    setMoveVerticalValue(lead.businessVertical || BUSINESS_VERTICALS[0]);
  };

  const handleMoveLead = async () => {
    if (!moveLead) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/leads/${moveLead.id}/vertical`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ businessVertical: moveVerticalValue })
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to move lead');
      }
      setMoveLead(null);
      if (moveVerticalValue !== selectedVertical) {
        setSelectedVertical(moveVerticalValue);
        setPage(1);
      }
      refreshLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (lead) => {
    setDeleteLead(lead);
  };

  const handleDeleteLead = async () => {
    if (!deleteLead) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/leads/${deleteLead.id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to delete lead');
      }
      setDeleteLead(null);
      refreshLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerticalChange = (vertical) => {
    setSelectedVertical(vertical);
    setPage(1);
    setCrmSyncResult(null);
  };

  const handleCrmSync = async () => {
    setCrmSyncLoading(true);
    setCrmSyncResult(null);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/api/crm/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessVertical: selectedVertical,
          limit: 100
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to sync with CRM');
      }

      const result = await response.json();
      setCrmSyncResult(result);
      
      if (result.synced > 0) {
        refreshLeads();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCrmSyncLoading(false);
    }
  };

  const totalPages = meta.totalPages || 0;
  const hasResults = leads.length > 0;
  const leadsWithWebsite = leads.filter(lead => Boolean(lead.website)).length;
  const leadsWithBookingPlatform = leads.filter(lead => Boolean(lead.bookingPlatform)).length;

  return (
    <div className="leads-tab">
      <div className="leads-header">
        <div>
          <h2>Leads Library</h2>
          <p className="leads-subtitle">
            Browse all saved leads, see who has a website, and spot booking-platform backlinks instantly.
          </p>
        </div>
        <div className="header-actions">
          <button
            className="secondary-button"
            onClick={handleCrmSync}
            disabled={crmSyncLoading}
          >
            {crmSyncLoading ? 'Syncing to CRM...' : 'Sync to SuiteCRM'}
          </button>
          <button
            className="secondary-button"
            onClick={handleDownloadAll}
            disabled={globalDownloadLoading}
          >
            {globalDownloadLoading ? 'Preparing...' : 'Download All'}
          </button>
          <button className="primary-button" onClick={handleDownload}>
            Download Excel
          </button>
        </div>
      </div>

      <div className="vertical-tabs">
        {BUSINESS_VERTICALS.map((vertical) => (
          <button
            key={vertical}
            className={`vertical-tab ${vertical === selectedVertical ? 'active' : ''}`}
            onClick={() => handleVerticalChange(vertical)}
          >
            {vertical}
          </button>
        ))}
      </div>

      <div className="leads-toolbar">
        <div className="search-field">
          <span className="search-icon">Search</span>
          <input
            type="text"
            placeholder="Search by name, city, state, or zip"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
        <div className="listing-count">
          {meta.total > 0
            ? `Showing ${leads.length} of ${meta.total} leads`
            : 'No leads to show yet'}
        </div>
      </div>

      <div className="lead-insights">
        <div className="insight-card">
          <span className="insight-label">Visible on this page</span>
          <strong>{leads.length}</strong>
        </div>
        <div className="insight-card">
          <span className="insight-label">Has website</span>
          <strong>{leadsWithWebsite}</strong>
        </div>
        <div className="insight-card">
          <span className="insight-label">Has booking platform</span>
          <strong>{leadsWithBookingPlatform}</strong>
        </div>
      </div>

      {error && (
        <div className="leads-error">
          {error}
        </div>
      )}

      {crmSyncResult && (
        <div className={`crm-sync-status ${crmSyncResult.synced > 0 ? 'success' : 'info'}`}>
          {crmSyncResult.synced > 0 
            ? `Successfully synced ${crmSyncResult.synced} leads to SuiteCRM.` 
            : crmSyncResult.message || 'No unsynced leads found for this vertical.'}
          {crmSyncResult.failed > 0 && ` (${crmSyncResult.failed} failed)`}
          <button className="close-status" onClick={() => setCrmSyncResult(null)}>×</button>
        </div>
      )}

      <div className="lead-cards-wrapper">
        <div className="lead-list">
          {!loading && hasResults && leads.map((lead) => {
            const bookingPlatforms = splitPlatforms(lead.bookingPlatform);
            const isExpanded = expandedLeadId === lead.id;

            return (
              <div className={`lead-list-item${isExpanded ? ' expanded' : ''}`} key={lead.id}>
                <button
                  className="lead-list-row"
                  onClick={() => setExpandedLeadId(prev => prev === lead.id ? null : lead.id)}
                >
                  <span className="lead-list-name">{lead.name || 'Untitled Lead'}</span>
                  <div className="lead-list-platforms">
                    {bookingPlatforms.length > 0 ? (
                      <div className="platform-chip-list">
                        {bookingPlatforms.map(platform => (
                          <span className="platform-chip" key={`${lead.id}-${platform}`}>
                            {platform}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="lead-no-platform">No booking platform</span>
                    )}
                  </div>
                  <span className="lead-list-chevron">{isExpanded ? '▲' : '▼'}</span>
                </button>

                {isExpanded && (
                  <div className="lead-detail">
                    <div className="lead-card-top">
                      <div>
                        <p className="lead-location">
                          {[lead.city, lead.state, lead.zip].filter(Boolean).join(', ') || 'Location unavailable'}
                        </p>
                      </div>
                      <div className="lead-status-stack">
                        {lead.crmSynced ? (
                          lead.crmLink ? (
                            <a href={lead.crmLink} target="_blank" rel="noreferrer" className="crm-link">
                              {lead.crmStatus || 'New'}
                            </a>
                          ) : (
                            <span className="crm-badge synced">{lead.crmStatus || 'Synced'}</span>
                          )
                        ) : (
                          <span className="crm-badge not-synced">Not Synced</span>
                        )}
                        <span className={`signal-pill ${lead.website ? 'positive' : 'neutral'}`}>
                          {lead.website ? 'Website Found' : 'No Website'}
                        </span>
                      </div>
                    </div>

                    <div className="lead-main-grid">
                      <div className="lead-data-panel">
                        <span className="panel-label">Address</span>
                        <p>{lead.address || 'No address available'}</p>
                      </div>
                      <div className="lead-data-panel">
                        <span className="panel-label">Contact</span>
                        <p>{lead.phone || 'No phone found'}</p>
                        <p>{lead.email || 'No email found'}</p>
                      </div>
                    </div>

                    <div className="lead-feature-strip">
                      <div className="feature-card">
                        <span className="panel-label">Website</span>
                        {lead.website ? (
                          <a href={normalizeUrl(lead.website)} target="_blank" rel="noreferrer">
                            {lead.website}
                          </a>
                        ) : (
                          <p>No website detected</p>
                        )}
                      </div>
                      <div className="feature-card booking-card">
                        <span className="panel-label">Booking Platforms</span>
                        {bookingPlatforms.length > 0 ? (
                          <div className="platform-chip-list">
                            {bookingPlatforms.map(platform => (
                              <span className="platform-chip" key={`${lead.id}-${platform}-detail`}>
                                {platform}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p>No booking backlink found on Google Maps</p>
                        )}
                      </div>
                    </div>

                    <div className="lead-hours">
                      <span className="panel-label">Business Hours</span>
                      <p>{lead.businessHours || 'Hours not available'}</p>
                    </div>

                    <div className="actions-cell">
                      <button className="ghost-button" onClick={() => openEditModal(lead)}>
                        Edit
                      </button>
                      <button className="ghost-button" onClick={() => openMoveModal(lead)}>
                        Move
                      </button>
                      <button className="danger-button" onClick={() => openDeleteModal(lead)}>
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!loading && !hasResults && (
          <div className="empty-state">
            No leads found for this business vertical.
          </div>
        )}

        {loading && (
          <div className="table-loading">Loading leads...</div>
        )}
      </div>

      <div className="pagination">
        <button
          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
          disabled={page <= 1 || loading}
        >
          Previous
        </button>
        <span>
          Page {totalPages > 0 ? page : 0} of {totalPages}
        </span>
        <button
          onClick={() => setPage(prev => (prev < totalPages ? prev + 1 : prev))}
          disabled={page >= totalPages || loading || totalPages === 0}
        >
          Next
        </button>
      </div>

      {editLead && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Edit Lead</h3>
            <div className="modal-grid">
              <label>
                Name
                <input
                  name="name"
                  value={editForm.name}
                  onChange={handleEditInputChange}
                />
              </label>
              <label className="wide">
                Full Address
                <input
                  name="address"
                  value={editForm.address}
                  onChange={handleEditInputChange}
                />
              </label>
              <label>
                Street
                <input
                  name="street"
                  value={editForm.street}
                  onChange={handleEditInputChange}
                />
              </label>
              <label>
                City
                <input
                  name="city"
                  value={editForm.city}
                  onChange={handleEditInputChange}
                />
              </label>
              <label>
                State
                <input
                  name="state"
                  value={editForm.state}
                  onChange={handleEditInputChange}
                />
              </label>
              <label>
                Zip
                <input
                  name="zip"
                  value={editForm.zip}
                  onChange={handleEditInputChange}
                />
              </label>
              <label>
                Phone
                <input
                  name="phone"
                  value={editForm.phone}
                  onChange={handleEditInputChange}
                />
              </label>
              <label>
                Website
                <input
                  name="website"
                  value={editForm.website}
                  onChange={handleEditInputChange}
                />
              </label>
              <label>
                Booking Platform
                <input
                  name="bookingPlatform"
                  value={editForm.bookingPlatform}
                  onChange={handleEditInputChange}
                />
              </label>
              <label>
                Email
                <input
                  name="email"
                  value={editForm.email}
                  onChange={handleEditInputChange}
                />
              </label>
              <label className="wide">
                Business Hours
                <textarea
                  name="businessHours"
                  value={editForm.businessHours}
                  onChange={handleEditInputChange}
                  rows="3"
                />
              </label>
            </div>
            <div className="modal-actions">
              <button
                className="ghost-button"
                onClick={() => setEditLead(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                onClick={handleSaveEdit}
                disabled={actionLoading}
              >
                {actionLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {moveLead && (
        <div className="modal-backdrop">
          <div className="modal small">
            <h3>Move Lead</h3>
            <p>Select the business vertical to move this lead into.</p>
            <select
              value={moveVerticalValue}
              onChange={(event) => setMoveVerticalValue(event.target.value)}
            >
              {BUSINESS_VERTICALS.map(vertical => (
                <option key={vertical} value={vertical}>
                  {vertical}
                </option>
              ))}
            </select>
            <div className="modal-actions">
              <button
                className="ghost-button"
                onClick={() => setMoveLead(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                onClick={handleMoveLead}
                disabled={actionLoading}
              >
                {actionLoading ? 'Moving...' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteLead && (
        <div className="modal-backdrop">
          <div className="modal small">
            <h3>Delete Lead</h3>
            <p>Are you sure you want to delete "{deleteLead.name || 'this lead'}"?</p>
            <div className="modal-actions">
              <button
                className="ghost-button"
                onClick={() => setDeleteLead(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="danger-button"
                onClick={handleDeleteLead}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadsTab;
